import { useEffect, useState } from 'react';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { useParams } from 'react-router-dom';
import { useAlertContext } from '../../../../../../context/AlertProvider';
import { useApi } from '../../../../../../context/ApiProvider';
import { KidInterface } from '../../../../../../shared/type/kid.type';

export type ChildrenStackInterface = {
  consumerId: number;
  firstName: string;
  lastName: string;
  cardNumber: string;
  organisationUnitId: number | null;
  saldo: string;
};

interface UseChildrenStackProps {
  usersIds: number[];
  classes: { label: string; value: number }[];
}

export const useChildrenStack = ({ usersIds, classes }: UseChildrenStackProps) => {
  const { apiTenantConsumerController } = useApi();
  const { id: tenantId } = useParams<{ id: string }>();
  const { setShow, setAlertProperties } = useAlertContext();

  const [childrenStack, setChildrenStack] = useState<ChildrenStackInterface[]>([]);
  const [actualUserId, setActualUserId] = useState<number>(Math.min(...usersIds));

  const [initialChildrenStack, setInitialChildrenStack] = useState<ChildrenStackInterface[]>([]);

  const fetchKids = async () => {
    try {
      if (!tenantId || !classes) return;
      const response: AxiosResponse<KidInterface[]> = await apiTenantConsumerController(
        'list-data'
      ).post('', {
        searchPhrase: '',
        tenantId: Number(tenantId)
      });

      const filteredChildren = response.data
        .filter((item) => usersIds.includes(Number(item.id)))
        .map((child) => {
          const childClassId = classes.find((item) => item.label === child.organisationUnitName);
          return {
            consumerId: child.id,
            firstName: child.firstName,
            lastName: child.lastName,
            cardNumber: child.cardNumber || '',
            organisationUnitId: childClassId?.value || null,
            saldo: child.saldo.replace(/\s*zł/g, '')
          };
        });
      setChildrenStack(filteredChildren);
      setInitialChildrenStack(filteredChildren);
    } catch (error) {
      console.error('Error fetching children:', error);
    }
  };

  useEffect(() => {
    fetchKids();
  }, [usersIds]);

  const handleInputChange = (key: keyof KidInterface, value: string | number | null) => {
    setChildrenStack((prevStack) =>
      prevStack.map((child) => {
        if (child.consumerId === actualUserId) {
          let newValue = value;
          if (key === 'saldo' && typeof value === 'string') {
            newValue = value
              .replace(/[^0-9\-.,]/g, '') // pozwól minus, cyfry, kropka, przecinek
              .replace(/,/g, '.') // przecinek -> kropka
              .replace(/(?!^)-/g, '') // pozwól tylko jeden minus NA POCZĄTKU
              .replace(/(\..*?)\..*/g, '$1'); // tylko jedna kropka
          }
          return { ...child, [key]: newValue };
        }
        return child;
      })
    );
  };

  const upgradeUsers = async (onSuccess?: () => void) => {
    try {
      const changedChildren = childrenStack.filter((child, index) => {
        const initial = initialChildrenStack.find((c) => c.consumerId === child.consumerId);
        return JSON.stringify(child) !== JSON.stringify(initial);
      });

      if (changedChildren.length === 0) {
        setAlertProperties({
          timeout: 5000,
          title: 'Brak zmian',
          description: 'Nie dokonano żadnych zmian do zapisania.',
          status: 'info'
        });
        setShow(true);
        return;
      }

      const response = await apiTenantConsumerController('mass-update').put(
        `/${tenantId}`,
        changedChildren
      );
      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: 'Sukces',
          description: 'Poprawnie zaktualizowano użytkowników',
          status: 'success'
        });
        setShow(true);
        fetchKids();
        onSuccess?.();
      }
    } catch (error: any) {
      setAlertProperties({
        timeout: 9000,
        title: 'Błąd',
        description: `${error.response?.data?.errorMessage || 'Nieznany błąd'}`,
        status: 'error'
      });
      setShow(true);
    }
  };

  return {
    childrenStack,
    actualUserId,
    setActualUserId,
    handleInputChange,
    upgradeUsers
  };
};
